import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Progress } from "../../components/ui/progress";
import { Badge } from "../../components/ui/badge";
import Pagination from "../components/Pagination";
import { toast } from "sonner";
import {
  Upload, Zap, Image as ImageIcon, X, Play, Pause, Square, RotateCcw, Trash2, ArrowLeft,
  CheckCircle2, XCircle, Clock, Bot, User as UserIcon, RefreshCw,
  Paperclip, ExternalLink,
} from "lucide-react";

const RESULTS_PER_PAGE = 25;
const POLL_INTERVAL_MS = 2000;

// =====================================================
// Página principal — alterna entre modo "lista+form" e
// modo "detalhe do lote" conforme :id na rota.
// =====================================================
export default function BulkIaPage() {
  const { id } = useParams();
  if (id) return <BatchDetail id={id} />;
  return <BatchListAndForm />;
}

// =====================================================
// Modo 1 — Form de criação + histórico
// =====================================================
function BatchListAndForm() {
  const navigate = useNavigate();

  const [prompt, setPrompt] = useState("");
  const [total, setTotal] = useState(1);
  const [concurrency, setConcurrency] = useState(10);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Imagem opcional
  const [imageUrl, setImageUrl] = useState("");
  const [imageName, setImageName] = useState("");
  const [imageUploading, setImageUploading] = useState(false);
  const imageInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const [submitting, setSubmitting] = useState(false);

  // Histórico
  const [history, setHistory] = useState({ items: [], total: 0, page: 1, limit: 10 });
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyPage, setHistoryPage] = useState(1);
  const [clearing, setClearing] = useState(false);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await api.get("/admin/bulk-ia", {
        params: { page: historyPage, limit: 10 },
      });
      setHistory(res.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setHistoryLoading(false);
    }
  }, [historyPage]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const handleClearAll = async () => {
    const totalCount = history.total || 0;
    if (totalCount === 0) {
      toast.info("Nenhum lote para limpar");
      return;
    }
    const ok = window.confirm(
      `Apagar TODOS os ${totalCount.toLocaleString("pt-BR")} lote(s) e suas respostas?\n` +
      `Lotes em execução serão cancelados. Esta ação é irreversível.`
    );
    if (!ok) return;
    setClearing(true);
    try {
      const res = await api.post("/admin/bulk-ia/delete-all");
      const dB = res.data?.deleted_batches ?? 0;
      toast.success(`${dB.toLocaleString("pt-BR")} lote(s) removido(s)`);
      setHistoryPage(1);
      await loadHistory();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setClearing(false);
    }
  };

  // Polling leve para atualizar o histórico se houver lotes "running"
  useEffect(() => {
    const hasRunning = history.items?.some((b) => b.status === "running");
    if (!hasRunning) return;
    const t = setInterval(loadHistory, 4000);
    return () => clearInterval(t);
  }, [history, loadHistory]);

  const handleImageFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Envie uma imagem (jpg, png, webp...)");
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error("Imagem muito grande (máx 25MB)");
      return;
    }
    setImageUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post("/admin/bulk-ia/upload-image", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImageUrl(res.data.url);
      setImageName(res.data.filename || file.name);
      toast.success("Imagem enviada");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setImageUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) handleImageFile(f);
  };

  const clearImage = () => {
    setImageUrl("");
    setImageName("");
    if (imageInputRef.current) imageInputRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const promptTrim = prompt.trim();
    if (!promptTrim && !imageUrl) {
      toast.error("Envie pelo menos um texto ou uma imagem");
      return;
    }
    const totalNum = Number(total);
    if (!Number.isFinite(totalNum) || totalNum < 1) {
      toast.error("Número de requisições inválido");
      return;
    }
    if (totalNum > 1_000_000) {
      toast.error("Máximo 1.000.000 requisições por lote");
      return;
    }
    if (totalNum > 1000) {
      const ok = window.confirm(
        `Você está prestes a disparar ${totalNum.toLocaleString("pt-BR")} chamadas à IA.\n` +
        `Cada chamada consome uma conta + tempo da API.\n\nDeseja continuar?`
      );
      if (!ok) return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/admin/bulk-ia", {
        prompt: promptTrim,
        image_url: imageUrl || undefined,
        image_name: imageName || undefined,
        total: totalNum,
        concurrency: Number(concurrency) || 10,
      });
      toast.success("Lote iniciado");
      navigate(`/admin/ia-massa/${res.data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil((history.total || 0) / 10));

  return (
    <div className="space-y-6">
      {/* Form */}
      <form
        onSubmit={handleSubmit}
        data-testid="bulk-ia-form"
        className="bg-white border border-slate-200 rounded-lg p-5 sm:p-6 space-y-5"
      >
        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-700">Mensagem para a IA</label>
          <Textarea
            data-testid="bulk-ia-prompt"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={4}
            placeholder="Ex.: Crie uma imagem de um gato laranja em estilo aquarela. Pode usar Markdown, instruções multi-passo, etc."
            className="resize-y min-h-[100px]"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-700">Imagem (opcional)</label>
          {imageUrl ? (
            <div className="flex items-center gap-3 p-3 border border-slate-200 rounded-md bg-slate-50">
              <div className="h-14 w-14 rounded overflow-hidden bg-white border border-slate-200 flex items-center justify-center shrink-0">
                <img
                  src={imageUrl}
                  alt={imageName}
                  className="h-full w-full object-cover"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{imageName}</p>
                <a
                  href={imageUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1"
                >
                  <ExternalLink className="h-3 w-3" />
                  Abrir
                </a>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={clearImage}
                data-testid="bulk-ia-clear-image"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              onClick={() => imageInputRef.current?.click()}
              className={`border-2 border-dashed rounded-md p-6 text-center cursor-pointer transition-colors ${
                dragOver ? "border-amber-400 bg-amber-50" : "border-slate-200 hover:border-slate-300 bg-slate-50/50"
              }`}
              data-testid="bulk-ia-image-drop"
            >
              <ImageIcon className="h-7 w-7 mx-auto text-slate-400 mb-2" />
              <p className="text-sm text-slate-600">
                {imageUploading ? "Enviando..." : "Arraste uma imagem aqui ou clique para escolher"}
              </p>
              <p className="text-xs text-slate-400 mt-1">JPG, PNG, WEBP — até 25MB</p>
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => handleImageFile(e.target.files?.[0])}
                data-testid="bulk-ia-image-input"
              />
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Quantas requisições?</label>
            <Input
              type="number"
              min={1}
              max={1000000}
              value={total}
              onChange={(e) => setTotal(e.target.value)}
              data-testid="bulk-ia-total"
            />
            <p className="text-xs text-slate-400">
              De 1 até 1.000.000. Cada chamada usa uma conta da API.
            </p>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">
              Concorrência <span className="text-xs text-slate-400">(opcional, max 50)</span>
            </label>
            <Input
              type="number"
              min={1}
              max={50}
              value={concurrency}
              onChange={(e) => setConcurrency(e.target.value)}
              data-testid="bulk-ia-concurrency"
            />
            <p className="text-xs text-slate-400">
              Quantos workers paralelos rodam ao mesmo tempo.
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-end pt-2">
          <Button
            type="submit"
            disabled={submitting || imageUploading}
            data-testid="bulk-ia-submit"
            className="bg-amber-500 hover:bg-amber-600 text-white"
          >
            {submitting ? (
              <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Iniciando...</>
            ) : (
              <><Play className="h-4 w-4 mr-2" /> Iniciar lote</>
            )}
          </Button>
        </div>
      </form>

      {/* Histórico de lotes */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Lotes recentes</h2>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={loadHistory}
              disabled={historyLoading}
              data-testid="bulk-ia-refresh-list"
            >
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${historyLoading ? "animate-spin" : ""}`} />
              Atualizar
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleClearAll}
              disabled={clearing || historyLoading || (history.total ?? 0) === 0}
              data-testid="bulk-ia-clear-all"
              className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
            >
              <Trash2 className={`h-3.5 w-3.5 mr-1.5 ${clearing ? "animate-pulse" : ""}`} />
              Limpar tudo
            </Button>
          </div>
        </div>

        {historyLoading && history.items?.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-400">Carregando...</div>
        ) : history.items?.length === 0 ? (
          <div className="p-10 text-center">
            <Zap className="h-8 w-8 mx-auto text-slate-300 mb-2" />
            <p className="text-sm text-slate-500">Nenhum lote ainda. Crie o primeiro acima.</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="bulk-ia-list">
            {history.items.map((b) => (
              <BatchListItem key={b.id} batch={b} onClick={() => navigate(`/admin/ia-massa/${b.id}`)} />
            ))}
          </ul>
        )}

        {totalPages > 1 && (
          <div className="border-t border-slate-100">
            <Pagination
              page={historyPage}
              pages={totalPages}
              total={history.total}
              limit={10}
              onChange={setHistoryPage}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function BatchListItem({ batch, onClick }) {
  const pct = batch.total > 0 ? Math.round((batch.completed / batch.total) * 100) : 0;
  return (
    <li
      onClick={onClick}
      className="px-5 py-4 hover:bg-slate-50 cursor-pointer transition-colors"
      data-testid={`bulk-ia-list-item-${batch.id}`}
    >
      <div className="flex items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <StatusBadge status={batch.status} />
            <span className="text-xs text-slate-400">
              {new Date(batch.created_at).toLocaleString("pt-BR")}
            </span>
          </div>
          <p className="text-sm text-slate-900 line-clamp-2">
            {batch.prompt || <span className="italic text-slate-400">(sem texto, apenas imagem)</span>}
          </p>
          <div className="flex items-center gap-3 mt-2 text-xs text-slate-500 flex-wrap">
            <span><b className="text-slate-700">{batch.total.toLocaleString("pt-BR")}</b> reqs</span>
            <span className="text-emerald-600">✓ {batch.success.toLocaleString("pt-BR")}</span>
            <span className="text-rose-600">✗ {batch.failed.toLocaleString("pt-BR")}</span>
            {batch.image_url && <span className="inline-flex items-center gap-1"><ImageIcon className="h-3 w-3" /> imagem</span>}
          </div>
        </div>
        <div className="w-28 sm:w-36 shrink-0">
          <Progress value={pct} className="h-2" />
          <p className="text-[11px] text-slate-500 text-right mt-1">{pct}%</p>
        </div>
      </div>
    </li>
  );
}

// =====================================================
// Modo 2 — Detalhe do lote (chat-like + progresso)
// =====================================================
function BatchDetail({ id }) {
  const navigate = useNavigate();

  const [batch, setBatch] = useState(null);
  const [percent, setPercent] = useState(0);
  const [avgLatency, setAvgLatency] = useState(0);
  const [loading, setLoading] = useState(true);

  const [results, setResults] = useState({ items: [], total: 0, page: 1, limit: RESULTS_PER_PAGE });
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("all");
  const [resultsLoading, setResultsLoading] = useState(true);

  const [cancelling, setCancelling] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [pausing, setPausing] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [restarting, setRestarting] = useState(false);

  const status = batch?.status;
  const isRunning = status === "running";
  const isPaused = status === "paused";
  const isActive = isRunning || isPaused; // workers podem estar vivos
  const canResume = status === "paused" || status === "interrupted" || status === "cancelled";
  const canRestart = !!batch && !isRunning; // pode reiniciar do zero quando não está rodando

  const loadBatch = useCallback(async () => {
    try {
      const res = await api.get(`/admin/bulk-ia/${id}`);
      setBatch(res.data.batch);
      setPercent(res.data.percent || 0);
      setAvgLatency(res.data.avg_latency_ms || 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadResults = useCallback(async () => {
    setResultsLoading(true);
    try {
      const params = { page, limit: RESULTS_PER_PAGE };
      if (statusFilter !== "all") params.status = statusFilter;
      const res = await api.get(`/admin/bulk-ia/${id}/results`, { params });
      setResults(res.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setResultsLoading(false);
    }
  }, [id, page, statusFilter]);

  useEffect(() => { loadBatch(); }, [loadBatch]);
  useEffect(() => { loadResults(); }, [loadResults]);

  // Polling ao vivo enquanto running ou paused
  useEffect(() => {
    if (!isActive) return;
    const t = setInterval(() => {
      loadBatch();
      loadResults();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [isActive, loadBatch, loadResults]);

  const handleCancel = async () => {
    if (!window.confirm("Parar este lote? Workers em execução serão interrompidos.")) return;
    setCancelling(true);
    try {
      await api.post(`/admin/bulk-ia/${id}/cancel`);
      toast.success("Lote parado");
      await loadBatch();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCancelling(false);
    }
  };

  const handlePause = async () => {
    setPausing(true);
    try {
      await api.post(`/admin/bulk-ia/${id}/pause`);
      toast.success("Lote pausado");
      await loadBatch();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPausing(false);
    }
  };

  const handleResume = async () => {
    setResuming(true);
    try {
      const res = await api.post(`/admin/bulk-ia/${id}/resume`);
      const mode = res.data?.mode;
      if (mode === "respawned") {
        const remaining = res.data?.remaining ?? 0;
        toast.success(`Lote retomado — ${remaining.toLocaleString("pt-BR")} requisição(ões) restantes`);
      } else {
        toast.success("Lote retomado");
      }
      await loadBatch();
      await loadResults();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setResuming(false);
    }
  };

  const handleRestart = async () => {
    const total = batch?.total || 0;
    const ok = window.confirm(
      `Reiniciar este lote do zero?\n\n` +
      `• Todas as ${(batch?.completed || 0).toLocaleString("pt-BR")} respostas atuais serão APAGADAS.\n` +
      `• O lote será executado novamente do início (${total.toLocaleString("pt-BR")} requisições).\n\n` +
      `Esta ação é irreversível.`
    );
    if (!ok) return;
    setRestarting(true);
    try {
      await api.post(`/admin/bulk-ia/${id}/restart`);
      toast.success("Lote reiniciado do zero");
      setPage(1);
      await loadBatch();
      await loadResults();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRestarting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Apagar este lote e todas as respostas? Esta ação é irreversível.")) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/bulk-ia/${id}`);
      toast.success("Lote removido");
      navigate("/admin/ia-massa");
    } catch (e) {
      toast.error(formatApiError(e));
      setDeleting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil((results.total || 0) / RESULTS_PER_PAGE));

  if (loading) {
    return <div className="p-8 text-center text-sm text-slate-400">Carregando lote...</div>;
  }
  if (!batch) {
    return (
      <div className="p-8 text-center">
        <p className="text-sm text-slate-500 mb-3">Lote não encontrado.</p>
        <Link to="/admin/ia-massa" className="text-sm text-blue-600 hover:underline">Voltar para lotes</Link>
      </div>
    );
  }

  const pending = Math.max(0, batch.total - batch.completed);

  return (
    <div className="space-y-5">
      {/* Header / breadcrumb */}
      <div className="flex items-center gap-3">
        <Link
          to="/admin/ia-massa"
          className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
          data-testid="bulk-ia-back"
        >
          <ArrowLeft className="h-4 w-4" /> Voltar para lotes
        </Link>
      </div>

      {/* Card resumo */}
      <div className="bg-white border border-slate-200 rounded-lg p-5 sm:p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <StatusBadge status={batch.status} />
              <span className="text-xs text-slate-400">
                Criado em {new Date(batch.created_at).toLocaleString("pt-BR")}
              </span>
              {batch.finished_at && (
                <span className="text-xs text-slate-400">
                  · Finalizado em {new Date(batch.finished_at).toLocaleString("pt-BR")}
                </span>
              )}
            </div>
            <div className="flex items-start gap-3">
              {batch.image_url && (
                <a
                  href={batch.image_url}
                  target="_blank"
                  rel="noreferrer"
                  className="h-16 w-16 rounded-md overflow-hidden border border-slate-200 shrink-0 bg-slate-100 flex items-center justify-center"
                >
                  <img
                    src={batch.image_url}
                    alt=""
                    className="h-full w-full object-cover"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                </a>
              )}
              <p className="text-sm text-slate-700 whitespace-pre-wrap break-words" data-testid="bulk-ia-prompt-display">
                {batch.prompt || <span className="italic text-slate-400">(somente imagem)</span>}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
            {isRunning && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handlePause}
                disabled={pausing}
                data-testid="bulk-ia-pause"
                className="text-amber-700 border-amber-200 hover:bg-amber-50 hover:text-amber-800"
              >
                <Pause className="h-3.5 w-3.5 mr-1.5" /> {pausing ? "Pausando..." : "Pausar"}
              </Button>
            )}
            {canResume && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleResume}
                disabled={resuming}
                data-testid="bulk-ia-resume"
                className="text-emerald-700 border-emerald-200 hover:bg-emerald-50 hover:text-emerald-800"
              >
                <Play className="h-3.5 w-3.5 mr-1.5" /> {resuming ? "Retomando..." : "Retomar"}
              </Button>
            )}
            {canRestart && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleRestart}
                disabled={restarting}
                data-testid="bulk-ia-restart"
                className="text-blue-700 border-blue-200 hover:bg-blue-50 hover:text-blue-800"
              >
                <RotateCcw className={`h-3.5 w-3.5 mr-1.5 ${restarting ? "animate-spin" : ""}`} /> Reiniciar
              </Button>
            )}
            {isActive && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCancel}
                disabled={cancelling}
                data-testid="bulk-ia-cancel"
                className="text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700"
              >
                <Square className="h-3.5 w-3.5 mr-1.5" /> {cancelling ? "Parando..." : "Parar"}
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleDelete}
              disabled={deleting || isActive}
              data-testid="bulk-ia-delete"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Apagar
            </Button>
          </div>
        </div>

        {/* Progresso */}
        <div className="mt-5 space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-600">
              Progresso · <b className="text-slate-900">{batch.completed.toLocaleString("pt-BR")}</b> de <b className="text-slate-900">{batch.total.toLocaleString("pt-BR")}</b>
            </span>
            <span className="font-semibold text-slate-900" data-testid="bulk-ia-percent">
              {percent.toFixed(1)}%
            </span>
          </div>
          <Progress value={percent} className="h-2.5" />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
            <Stat
              label="Sucesso"
              value={batch.success.toLocaleString("pt-BR")}
              tone="emerald"
              icon={<CheckCircle2 className="h-4 w-4" />}
            />
            <Stat
              label="Falha"
              value={batch.failed.toLocaleString("pt-BR")}
              tone="rose"
              icon={<XCircle className="h-4 w-4" />}
            />
            <Stat
              label="Pendentes"
              value={pending.toLocaleString("pt-BR")}
              tone="slate"
              icon={<Clock className="h-4 w-4" />}
            />
            <Stat
              label="Latência média"
              value={avgLatency > 0 ? `${(avgLatency / 1000).toFixed(1)}s` : "—"}
              tone="slate"
              icon={<RefreshCw className="h-4 w-4" />}
            />
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          {["all", "success", "failed"].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => { setStatusFilter(s); setPage(1); }}
              data-testid={`bulk-ia-filter-${s}`}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                statusFilter === s
                  ? "bg-slate-900 text-white"
                  : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
            >
              {s === "all" ? "Todos" : s === "success" ? "Sucesso" : "Falha"}
            </button>
          ))}
        </div>
        <div className="text-xs text-slate-500">
          {results.total.toLocaleString("pt-BR")} resultado{results.total === 1 ? "" : "s"}
        </div>
      </div>

      {/* Chat-like results */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {resultsLoading && results.items.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-400">Carregando respostas...</div>
        ) : results.items.length === 0 ? (
          <div className="p-12 text-center">
            <Bot className="h-8 w-8 mx-auto text-slate-300 mb-2" />
            <p className="text-sm text-slate-500">
              {isRunning ? "Aguardando primeiras respostas..." : "Nenhum resultado neste filtro."}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="bulk-ia-results">
            {results.items.map((r) => (
              <ResultItem key={r.id} item={r} batch={batch} />
            ))}
          </ul>
        )}

        {totalPages > 1 && (
          <div className="border-t border-slate-100">
            <Pagination
              page={page}
              pages={totalPages}
              total={results.total}
              limit={RESULTS_PER_PAGE}
              onChange={setPage}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, tone, icon }) {
  const tones = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
    rose: "bg-rose-50 text-rose-700 border-rose-100",
    slate: "bg-slate-50 text-slate-700 border-slate-200",
  };
  return (
    <div className={`border rounded-md p-3 ${tones[tone] || tones.slate}`}>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide opacity-80">
        {icon}
        {label}
      </div>
      <div className="text-xl font-semibold mt-1">{value}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    running:     { label: "Em execução", cn: "bg-blue-50 text-blue-700 border-blue-200" },
    paused:      { label: "Pausado",     cn: "bg-amber-50 text-amber-700 border-amber-200" },
    completed:   { label: "Concluído",   cn: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    cancelled:   { label: "Parado",      cn: "bg-slate-100 text-slate-600 border-slate-200" },
    interrupted: { label: "Interrompido", cn: "bg-amber-50 text-amber-700 border-amber-200" },
    success:     { label: "Sucesso",     cn: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    failed:      { label: "Falha",       cn: "bg-rose-50 text-rose-700 border-rose-200" },
    pending:     { label: "Pendente",    cn: "bg-slate-100 text-slate-600 border-slate-200" },
  };
  const item = map[status] || { label: status, cn: "bg-slate-100 text-slate-600 border-slate-200" };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium border ${item.cn}`}>
      {item.label}
    </span>
  );
}

function ResultItem({ item, batch }) {
  const isOk = item.status === "success";
  return (
    <li className="px-5 py-4" data-testid={`bulk-ia-result-${item.index}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-mono text-slate-400">#{item.index.toLocaleString("pt-BR")}</span>
          <StatusBadge status={item.status} />
          {item.latency_ms > 0 && (
            <span className="text-[11px] text-slate-400">
              · {(item.latency_ms / 1000).toFixed(1)}s
            </span>
          )}
        </div>
        <span className="text-[11px] text-slate-400">
          {new Date(item.created_at).toLocaleTimeString("pt-BR")}
        </span>
      </div>

      {/* Mensagem do usuário (prompt do lote) */}
      <div className="flex items-start gap-3 mb-3">
        <div className="h-7 w-7 rounded-full bg-slate-200 flex items-center justify-center shrink-0">
          <UserIcon className="h-3.5 w-3.5 text-slate-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-slate-400 mb-1">Pedido</p>
          <div className="text-sm text-slate-700 whitespace-pre-wrap break-words bg-slate-50 border border-slate-100 rounded-md p-3">
            {batch.prompt || <span className="italic text-slate-400">(somente imagem)</span>}
            {batch.image_url && (
              <div className="mt-2 inline-flex items-center gap-1 text-xs text-slate-500">
                <Paperclip className="h-3 w-3" /> {batch.image_name || "imagem"}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Resposta da IA */}
      <div className="flex items-start gap-3">
        <div className={`h-7 w-7 rounded-full flex items-center justify-center shrink-0 ${
          isOk ? "bg-amber-100" : "bg-rose-100"
        }`}>
          <Bot className={`h-3.5 w-3.5 ${isOk ? "text-amber-700" : "text-rose-700"}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-slate-400 mb-1">Resposta</p>
          {isOk ? (
            <>
              <div className="text-sm text-slate-800 whitespace-pre-wrap break-words bg-amber-50/50 border border-amber-100 rounded-md p-3">
                {item.response || <span className="italic text-slate-400">(resposta vazia)</span>}
              </div>
              {item.attachments && item.attachments.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {item.attachments.map((a, i) => (
                    <AttachmentChip key={i} att={a} />
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="text-sm text-rose-800 whitespace-pre-wrap break-words bg-rose-50 border border-rose-100 rounded-md p-3">
              {item.error || "Falha desconhecida"}
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

function AttachmentChip({ att }) {
  const url = att?.url || att?.URL || "";
  const name = att?.name || att?.Name || "arquivo";
  const isImage = /\.(png|jpe?g|webp|gif|svg)(\?|$)/i.test(url);
  if (!url) return null;
  if (isImage) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="block h-20 w-20 rounded-md overflow-hidden border border-slate-200 bg-slate-100"
        title={name}
      >
        <img src={url} alt={name} className="h-full w-full object-cover" />
      </a>
    );
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-slate-200 rounded-md text-xs text-slate-700 hover:bg-slate-50 max-w-full"
    >
      <Paperclip className="h-3 w-3 shrink-0" />
      <span className="truncate max-w-[200px]">{name}</span>
      <ExternalLink className="h-3 w-3 shrink-0 opacity-50" />
    </a>
  );
}
